import React from 'react';
import { useRouter } from '../context/RouterContext';

const About: React.FC = () => {
  const { navigate } = useRouter();

  const backButtonClass = "flex items-center text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors group";
  const cardClass = "bg-white dark:bg-slate-800/50 rounded-2xl shadow-soft dark:shadow-soft-dark p-6 border border-gray-200/50 dark:border-slate-700/50";
  const headingClass = "text-gray-900 dark:text-white";
  const textClass = "text-gray-600 dark:text-slate-400";

  return (
    <div className="max-w-4xl mx-auto px-3 py-6">
      <div className={cardClass}>
        <button onClick={() => navigate('home')} className={backButtonClass}>
          <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>
        
        <h1 className={`text-4xl font-bold ${headingClass} mb-4`}>About Rate My Plate</h1>
        
        <div className="prose prose-lg max-w-none">
          <p className={`${textClass} mb-4 text-lg leading-relaxed`}>
            Rate My Plate is a fun community platform where people can share and rate custom license plates from around the world. 
            Looking for funny license plate ideas or inspiration for your own personalized plate? Browse thousands of submissions, 
            from clever vanity plates to hilarious wordplay. Whether you've spotted a gem or want to show off your own, this is 
            the place to celebrate the creativity and humor found on our roads.
          </p>

          <h2 className={`text-2xl font-semibold ${headingClass} mb-3 mt-6`}>How It Works</h2>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 dark:bg-primary-500/10 p-4 rounded-xl">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold mr-3">1</div>
                <h3 className={`text-lg font-semibold ${headingClass}`}>Upload</h3>
              </div>
              <p className={textClass}>Take a photo of a custom license plate and upload it to share with the community.</p>
            </div>
            
            <div className="bg-green-50 dark:bg-green-500/10 p-4 rounded-xl">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-bold mr-3">2</div>
                <h3 className={`text-lg font-semibold ${headingClass}`}>Rate</h3>
              </div>
              <p className={textClass}>Vote on license plates you love (or don't love) to help the community find the best ones.</p>
            </div>
            
            <div className="bg-purple-50 dark:bg-purple-500/10 p-4 rounded-xl">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold mr-3">3</div>
                <h3 className={`text-lg font-semibold ${headingClass}`}>Browse</h3>
              </div>
              <p className={textClass}>Discover amazing license plates from around the world, sorted by newest or highest rated.</p>
            </div>
            
            <div className="bg-orange-50 dark:bg-orange-500/10 p-4 rounded-xl">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold mr-3">4</div>
                <h3 className={`text-lg font-semibold ${headingClass}`}>Share</h3>
              </div>
              <p className={textClass}>Share your favorite finds with friends and family to spread the joy of creative license plates.</p>
            </div>
          </div>

          <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>Community Guidelines</h2>
          <div className="bg-gray-50 dark:bg-slate-700/30 p-4 rounded-xl mb-6">
            <ul className="space-y-3 text-gray-600 dark:text-slate-400">
              <li className="flex items-start"><span className="text-green-600 dark:text-green-400 mr-2">✓</span><span>Be respectful and kind to other community members</span></li>
              <li className="flex items-start"><span className="text-green-600 dark:text-green-400 mr-2">✓</span><span>Only upload photos of license plates (not personal information)</span></li>
              <li className="flex items-start"><span className="text-green-600 dark:text-green-400 mr-2">✓</span><span>Keep it fun and lighthearted - this is about celebrating creativity</span></li>
              <li className="flex items-start"><span className="text-green-600 dark:text-green-400 mr-2">✓</span><span>Don't upload inappropriate or offensive content</span></li>
              <li className="flex items-start"><span className="text-green-600 dark:text-green-400 mr-2">✓</span><span>Respect privacy - don't include faces or personal details in photos</span></li>
            </ul>
          </div>

          <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>Privacy & Safety</h2>
          <p className={`${textClass} mb-3`}>
            We take privacy seriously. All uploaded images are stored securely and we don't collect personal information 
            beyond what's necessary to provide the service. Your votes are tracked anonymously to prevent spam.
          </p>
          <p className={`${textClass} mb-6`}>
            If you see any inappropriate content, please report it and we'll review it promptly. We want to keep 
            Rate My Plate a fun and safe place for everyone.
          </p>

          <h2 className={`text-2xl font-semibold ${headingClass} mb-3`}>Contact</h2>
          <p className={`${textClass} mb-3`}>
            Have questions, suggestions, or found a bug? We'd love to hear from you!
          </p>
          <div className="bg-primary-50 dark:bg-primary-500/10 p-4 rounded-xl">
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gray-600 dark:bg-slate-600 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-500">Email</p>
                  <a href="mailto:r8mypl8@gmail.com" className="text-gray-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">r8mypl8@gmail.com</a>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-black dark:bg-slate-700 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-500">TikTok</p>
                  <a href="https://www.tiktok.com/@r8mypl81" target="_blank" rel="noopener noreferrer" className="text-gray-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">@r8mypl81</a>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-black dark:bg-slate-700 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-500">X (Twitter)</p>
                  <a href="https://twitter.com/R8MYPL8" target="_blank" rel="noopener noreferrer" className="text-gray-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">@R8MYPL8</a>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-slate-500">Instagram</p>
                  <a href="https://instagram.com/r8.my.pl8.app" target="_blank" rel="noopener noreferrer" className="text-gray-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">@r8.my.pl8.app</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
